import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { FiPlusCircle, FiCheck, FiX, FiRefreshCw, FiEdit2, FiTrash2 } from 'react-icons/fi';

const LEAVE_TYPES = ['Sick', 'Casual', 'Annual', 'Maternity', 'Paternity', 'Unpaid'];

const LeaveList = () => {
  const { user } = useAuth();
  const isAdminHR = user.role === 'admin' || user.role === 'hr';
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ leaveType: 'Sick', fromDate: '', toDate: '', reason: '', employee: '' });
  const [editLeave, setEditLeave] = useState(null); // leave object being edited
  const [editForm, setEditForm] = useState({ leaveType: 'Sick', fromDate: '', toDate: '', reason: '' });
  const todayStr = new Date().toISOString().split('T')[0];
  const [filterFromDate, setFilterFromDate] = useState(todayStr);
  const [filterToDate, setFilterToDate] = useState(todayStr);
  const [filterStatus, setFilterStatus] = useState('');

  const isManager = !isAdminHR && leaves.some(l => l.employee?._id !== user.employeeId && l.employee?._id !== user._id);

  const fetchLeaves = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterFromDate && filterToDate) { params.fromDate = filterFromDate; params.toDate = filterToDate; }
      const res = await api.get('/leaves', { params });
      setLeaves(res.data);
    } catch (err) { toast.error('Failed to fetch leaves'); }
    finally { setLoading(false); }
  }, [filterStatus, filterFromDate, filterToDate]);

  useEffect(() => { fetchLeaves(); }, [fetchLeaves]);

  useEffect(() => {
    if (isAdminHR) {
      api.get('/employees', { params: { limit: 100 } }).then(res => setEmployees(res.data.employees || [])).catch(() => {});
    }
    if (!isAdminHR && user.employeeId) {
      api.get(`/leaves/balance/${user.employeeId}`).then(res => setBalance(res.data)).catch(() => {});
    }
  }, [isAdminHR, user.employeeId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/leaves', form);
      toast.success('Leave applied');
      setShowForm(false);
      setForm({ leaveType: 'Sick', fromDate: '', toDate: '', reason: '', employee: '' });
      fetchLeaves();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to apply leave'); }
  };

  const handleAction = async (id, action) => {
    const remarks = window.prompt('Remarks (optional):') || '';
    try {
      await api.put(`/leaves/${id}/${action}`, { remarks });
      toast.success(`Leave ${action}d`);
      fetchLeaves();
    } catch (err) { toast.error(`Failed to ${action} leave`); }
  };

  const openEdit = (l) => {
    const fmt = (d) => new Date(d).toISOString().split('T')[0];
    setEditLeave(l);
    setEditForm({ leaveType: l.leaveType, fromDate: fmt(l.fromDate), toDate: fmt(l.toDate), reason: l.reason });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/leaves/${editLeave._id}`, editForm);
      toast.success('Leave updated');
      setEditLeave(null);
      fetchLeaves();
    } catch (err) { toast.error(err.response?.data?.message || 'Update failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this leave request?')) return;
    try {
      await api.delete(`/leaves/${id}`);
      toast.success('Leave request deleted');
      fetchLeaves();
    } catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Leave Management</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn-secondary" onClick={fetchLeaves} title="Refresh"><FiRefreshCw /></button>
          <button className="btn-primary" onClick={() => setShowForm(!showForm)}><FiPlusCircle /> {showForm ? 'Cancel' : 'Apply Leave'}</button>
        </div>
      </div>

      {/* Leave balance — employees only */}
      {!isAdminHR && balance && (
        <div className="form-card" style={{ marginBottom: '1rem' }}>
          <h3>Leave Balance</h3>
          <div className="form-grid">
            {['sick', 'casual', 'annual'].map(t => (
              <div key={t} className="detail-item"><label>{t.charAt(0).toUpperCase() + t.slice(1)}</label><span>{balance[t]} days</span></div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="form-card">
          <h3>Apply for Leave</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              {isAdminHR && (
                <div className="form-group"><label>Employee</label>
                  <select name="employee" value={form.employee} onChange={e => setForm({ ...form, employee: e.target.value })}>
                    <option value="">Self</option>
                    {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group"><label>Leave Type</label>
                <select value={form.leaveType} onChange={e => setForm({ ...form, leaveType: e.target.value })}>
                  {LEAVE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group"><label>From Date</label><input type="date" value={form.fromDate} onChange={e => setForm({ ...form, fromDate: e.target.value })} required /></div>
              <div className="form-group"><label>To Date</label><input type="date" value={form.toDate} onChange={e => setForm({ ...form, toDate: e.target.value })} required /></div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>Reason</label><textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} required rows={3} /></div>
            </div>
            <button type="submit" className="btn-primary" style={{ marginTop: '1rem' }}>Submit</button>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="filters-row" style={{ marginTop: '1rem' }}>
        <input type="date" value={filterFromDate} onChange={e => setFilterFromDate(e.target.value)} title="From date"
          style={{ padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem' }} />
        <input type="date" value={filterToDate} onChange={e => setFilterToDate(e.target.value)} title="To date"
          style={{ padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem' }} />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
        {(filterFromDate || filterToDate || filterStatus) && (
          <button className="btn-secondary" style={{ padding: '0.5rem 0.75rem' }}
            onClick={() => { setFilterFromDate(''); setFilterToDate(''); setFilterStatus(''); }}>
            Clear
          </button>
        )}
      </div>

      {isManager && (
        <div style={{ margin: '0.75rem 0 0.25rem', fontWeight: 600, color: '#4f46e5', fontSize: '0.95rem' }}>
          Team Leave Requests
        </div>
      )}
      <table className="table" style={{ marginTop: '0.5rem' }}>
        <thead>
          <tr>
            <th>Employee</th>
            <th>Type</th><th>From</th><th>To</th><th>Reason</th><th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {leaves.map(l => (
            <tr key={l._id}>
              <td>{l.employee?.name || 'N/A'}</td>
              <td>{l.leaveType}</td>
              <td>{new Date(l.fromDate).toLocaleDateString()}</td>
              <td>{new Date(l.toDate).toLocaleDateString()}</td>
              <td>{l.reason}</td>
              <td><span className={`status-badge status-${l.status.toLowerCase()}`}>{l.status}</span></td>
              <td className="actions">
                {isAdminHR && l.status === 'Pending' && (<>
                  <button className="btn-sm btn-success" onClick={() => handleAction(l._id, 'approve')} title="Approve"><FiCheck /></button>
                  <button className="btn-sm btn-danger" onClick={() => handleAction(l._id, 'reject')} title="Reject"><FiX /></button>
                </>)}
                {!isAdminHR && l.status === 'Pending' &&
                  (l.employee?._id === user.employeeId || l.employee?._id === user._id) && (<>
                  <button className="btn-sm" style={{ background: '#ede9fe', color: '#7c3aed', border: 'none' }} onClick={() => openEdit(l)} title="Edit"><FiEdit2 /></button>
                  <button className="btn-sm btn-danger" onClick={() => handleDelete(l._id)} title="Delete"><FiTrash2 /></button>
                </>)}
              </td>
            </tr>
          ))}
          {leaves.length === 0 && (
            <tr><td colSpan={7} style={{ textAlign: 'center' }}>No leave records</td></tr>
          )}
        </tbody>
      </table>
      {/* Edit Leave Modal */}
      {editLeave && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="form-card" style={{ width: '100%', maxWidth: 480, margin: '1rem' }}>
            <h3><FiEdit2 style={{ marginRight: '0.4rem' }} /> Edit Leave Request</h3>
            <form onSubmit={handleEdit}>
              <div className="form-grid">
                <div className="form-group"><label>Leave Type</label>
                  <select value={editForm.leaveType} onChange={e => setEditForm({ ...editForm, leaveType: e.target.value })}>
                    {LEAVE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>From Date</label>
                  <input type="date" value={editForm.fromDate} onChange={e => setEditForm({ ...editForm, fromDate: e.target.value })} required />
                </div>
                <div className="form-group"><label>To Date</label>
                  <input type="date" value={editForm.toDate} onChange={e => setEditForm({ ...editForm, toDate: e.target.value })} required />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>Reason</label>
                  <textarea value={editForm.reason} onChange={e => setEditForm({ ...editForm, reason: e.target.value })} required rows={3} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button type="submit" className="btn-primary">Save Changes</button>
                <button type="button" className="btn-secondary" onClick={() => setEditLeave(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveList;
